import { useCallback } from "react";
import { Button, ListGroup, Modal } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import SubmitButton from "../components/SubmitButton";
import Loading from "../Loading";
import { useDeleteSleeve, useQuerySleeveAssignments } from "./lib";

type SleeveDeleteDialogProps = {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  deleteSleeveId: number;
  setDeleteSleeveId: React.Dispatch<React.SetStateAction<number>>;
};

const SleeveDeleteDialogOfId = ({
  showModal,
  setShowModal,
  deleteSleeveId,
  setDeleteSleeveId,
}: SleeveDeleteDialogProps) => {
  const navigate = useNavigate();

  // get models using the sleeve
  const { isLoading: isLoadingAssignments, data: sleeveAssignments } =
    useQuerySleeveAssignments(deleteSleeveId);

  const handleModalClose = useCallback(() => {
    setDeleteSleeveId(0);
    setShowModal(false);
  }, [setDeleteSleeveId, setShowModal]);

  const deleteSleeve = useDeleteSleeve(deleteSleeveId);

  const handleDeleteSleeve = useCallback(async () => {
    await deleteSleeve.mutateAsync();
    setShowModal(false);
    navigate("/models/sleeves");
  }, [deleteSleeve, navigate, setShowModal]);

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      aria-labelledby="contained-modal-title-vcenter"
      scrollable={true}
      centered
    >
      <Modal.Header>
        <Modal.Title>Delete Sleeve</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingAssignments ? (
          <Loading />
        ) : sleeveAssignments?.length === 0 ? (
          <div className="m-2">
            Are you sure you would like to delete this sleeve?
          </div>
        ) : (
          <>
            <div className="m-2">
              This sleeve is attached to the below models. First, please
              un-attach the sleeve from below models.
            </div>
            <ListGroup as="ul">
              {sleeveAssignments?.map((model) => (
                <ListGroup.Item key={model.id} as="li">
                  <Link to={`/models/models/${model.id}`}>
                    <span>{model.name}</span>
                  </Link>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose}>
          Discard
        </Button>
        <SubmitButton
          hidden={sleeveAssignments?.length !== 0}
          variant="danger"
          label="Delete"
          onClick={handleDeleteSleeve}
        />
      </Modal.Footer>
    </Modal>
  );
};

/**
 * The dialog for one id. The assignment list is walked a page at a time and
 * collected per mounted hook (`lib/pagination.ts`), so the dialog is mounted
 * only for a real id and remounted when the id changes (APP-1650).
 */
const SleeveDeleteDialog = (props: SleeveDeleteDialogProps) =>
  props.deleteSleeveId === 0 ? null : (
    <SleeveDeleteDialogOfId key={props.deleteSleeveId} {...props} />
  );

export default SleeveDeleteDialog;
